import { useCallback, useSyncExternalStore } from 'react';

type SetValue<T> = T | ((prev: T) => T);

interface UseLocalStorageSyncOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
}

const LOCAL_STORAGE_CHANGE_EVENT = 'PROJECT_ID_LOCAL_STORAGE_CHANGE';

export function useLocalStorageSync<T>(
  key: string,
  initialValue: T,
  options: UseLocalStorageSyncOptions<T> = {}
): [T, (next: SetValue<T>) => void, () => void] {
  const serialize = options.serialize ?? JSON.stringify;
  const deserialize =
    options.deserialize ??
    ((raw: string) => {
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as T;
      }
    });

  const getSnapshot = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return initialValue;
    }

    try {
      return deserialize(raw);
    } catch {
      return initialValue;
    }
  }, [deserialize, initialValue, key]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined') {
        return () => {};
      }

      const onStorage = (event: StorageEvent) => {
        if (event.key === key) {
          onStoreChange();
        }
      };

      const onLocalStorageChange = (event: Event) => {
        const customEvent = event as CustomEvent<{ key: string }>;
        if (customEvent.detail?.key === key) {
          onStoreChange();
        }
      };

      window.addEventListener('storage', onStorage);
      window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, onLocalStorageChange);

      return () => {
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, onLocalStorageChange);
      };
    },
    [key]
  );

  const storageValue = useSyncExternalStore(subscribe, getSnapshot, () => initialValue);

  const setStorageValue = useCallback(
    (next: SetValue<T>) => {
      if (typeof window === 'undefined') {
        return;
      }

      const resolvedValue = next instanceof Function ? next(getSnapshot()) : next;
      const serialized = serialize(resolvedValue);
      window.localStorage.setItem(key, serialized);
      window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, { detail: { key } }));
    },
    [getSnapshot, key, serialize]
  );

  const removeValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(key);
    window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, { detail: { key } }));
  }, [key]);

  return [storageValue, setStorageValue, removeValue];
}
